import { useState, useEffect, useRef } from 'react';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, useDroppable, DragOverlay } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComment } from '@fortawesome/free-solid-svg-icons';
import toast from 'react-hot-toast';
import DiscussCardDetailModal from './DiscussCardDetailModal';
import { useAuth } from '../../contexts/AuthContext';
import { useFamily } from '../../contexts/FamilyContext';

export default function DiscussPhase({ experienceId, experience }) {
  const { user } = useAuth();
  const { family } = useFamily();
  const [columns, setColumns] = useState({
    later: { title: 'Later', cards: [] },
    'to-discuss': { title: 'To Discuss', cards: [] },
    'in-discussion': { title: 'In Discussion', cards: [] },
    decided: { title: 'Decided', cards: [] }
  });

  const [selectedCard, setSelectedCard] = useState(null);
  const [activeId, setActiveId] = useState(null);
  const isUpdatingRef = useRef(false);
  const originalSourceRef = useRef({ column: null, index: -1 });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px of movement before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    // Only reorganize if not currently dragging or updating
    if (activeId || isUpdatingRef.current) return;
    
    // Organize cards into columns
    const organized = {
      later: { title: 'Later', cards: [] },
      'to-discuss': { title: 'To Discuss', cards: [] },
      'in-discussion': { title: 'In Discussion', cards: [] },
      decided: { title: 'Decided', cards: [] }
    };

    const decisionCards = experience.decisionCards || [];
    decisionCards
      .filter(card => card.status !== 'dismissed')
      .forEach(card => {
        const column = card.kanbanColumn || card.status || 'to-discuss';
        if (organized[column]) {
          organized[column].cards.push(card);
        }
      });

    setColumns(organized);
  }, [experience.decisionCards, activeId]);

  const handleDragStart = (event) => {
    const cardId = event.active.id;
    setActiveId(cardId);
    
    // Store original source position
    Object.entries(columns).forEach(([colId, col]) => {
      const index = col.cards.findIndex(c => c.id === cardId);
      if (index !== -1) {
        originalSourceRef.current = { column: colId, index };
      }
    });
  };

  const handleDragOver = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const sourceColumn = originalSourceRef.current.column;
    const originalSourceIndex = originalSourceRef.current.index;
    
    if (!sourceColumn || originalSourceIndex === -1) return;

    // Find destination
    let destColumn = null;
    let destIndex = -1;

    // Check if dragging over another card
    Object.entries(columns).forEach(([colId, col]) => {
      const index = col.cards.findIndex(c => c.id === over.id);
      if (index !== -1) {
        destColumn = colId;
        destIndex = index;
      }
    });

    // Check if dragging over a column (empty area)
    if (!destColumn) {
      const overId = over.id.toString();
      if (overId.startsWith('column-')) {
        destColumn = overId.replace('column-', '');
        const destColCards = columns[destColumn]?.cards || [];
        // When dropping on empty column, put at index 0 (top)
        // If column has cards, put at the end
        destIndex = destColCards.length === 0 ? 0 : destColCards.length;
      }
    }

    if (!destColumn) return;

    // Update local state immediately for visual feedback
    const newColumns = { ...columns };
    
    // If same column, use arrayMove with original indices
    if (sourceColumn === destColumn) {
      const cards = [...newColumns[sourceColumn].cards];
      // For same-column moves, use original source index
      const newCards = arrayMove(cards, originalSourceIndex, destIndex);
      newColumns[sourceColumn] = { ...newColumns[sourceColumn], cards: newCards };
    } else {
      // Cross-column move - find current position in source column
      const sourceCards = [...newColumns[sourceColumn].cards];
      const currentSourceIndex = sourceCards.findIndex(c => c.id === active.id);
      if (currentSourceIndex === -1) return;
      
      const card = sourceCards[currentSourceIndex];
      
      // Remove from source
      sourceCards.splice(currentSourceIndex, 1);
      newColumns[sourceColumn] = { ...newColumns[sourceColumn], cards: sourceCards };

      // Add to destination
      const destCards = [...(newColumns[destColumn].cards || [])];
      destCards.splice(destIndex, 0, card);
      newColumns[destColumn] = { ...newColumns[destColumn], cards: destCards };
    }

    setColumns(newColumns);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) {
      // Reset columns if dropped in same position
      setActiveId(null);
      originalSourceRef.current = { column: null, index: -1 };
      const organized = {
        later: { title: 'Later', cards: [] },
        'to-discuss': { title: 'To Discuss', cards: [] },
        'in-discussion': { title: 'In Discussion', cards: [] },
        decided: { title: 'Decided', cards: [] }
      };

      const decisionCards = experience.decisionCards || [];
      decisionCards
        .filter(card => card.status !== 'dismissed')
        .forEach(card => {
          const column = card.kanbanColumn || card.status || 'to-discuss';
          if (organized[column]) {
            organized[column].cards.push(card);
          }
        });

      setColumns(organized);
      return;
    }

    // Use the current columns state (already updated by handleDragOver)
    // to get the final positions
    let sourceColumn = null;
    let destColumn = null;
    let sourceIndex = -1;
    let destIndex = -1;

    // Find where the card currently is (after handleDragOver updates)
    Object.entries(columns).forEach(([colId, col]) => {
      const index = col.cards.findIndex(c => c.id === active.id);
      if (index !== -1) {
        sourceColumn = colId;
        sourceIndex = index;
      }
    });

    // Find destination - check if over another card first
    Object.entries(columns).forEach(([colId, col]) => {
      const index = col.cards.findIndex(c => c.id === over.id);
      if (index !== -1) {
        destColumn = colId;
        // If same column, the card is already in the right position from handleDragOver
        if (colId === sourceColumn) {
          destIndex = sourceIndex; // Already positioned correctly
        } else {
          destIndex = index;
        }
      }
    });

    // If dropping on empty column area
    if (!destColumn) {
      const overId = over.id.toString();
      if (overId.startsWith('column-')) {
        destColumn = overId.replace('column-', '');
        // If moving to a different column, use the current length
        if (destColumn !== sourceColumn) {
          destIndex = columns[destColumn]?.cards.length || 0;
        } else {
          destIndex = sourceIndex; // Already positioned correctly
        }
      }
    }

    if (!sourceColumn || !destColumn) {
      setActiveId(null);
      originalSourceRef.current = { column: null, index: -1 };
      return;
    }

    const cardId = active.id;
    const newColumn = destColumn;

    // State is already updated in handleDragOver, just need to persist to Firestore
    // Set flag to prevent useEffect from reorganizing during update
    isUpdatingRef.current = true;

    // Rebuild decisionCards array from current columns state to preserve order
    const updatedCards = [];
    const allCards = new Map((experience.decisionCards || []).map(c => [c.id, c]));
    
    // Add cards in the order they appear in columns
    Object.values(columns).forEach(column => {
      column.cards.forEach(card => {
        const originalCard = allCards.get(card.id);
        if (originalCard) {
          // Update column and status if it changed
          const columnId = Object.keys(columns).find(
            colId => columns[colId].cards.some(c => c.id === card.id)
          );
          updatedCards.push({
            ...originalCard,
            kanbanColumn: columnId || originalCard.kanbanColumn,
            status: columnId || originalCard.status
          });
        }
      });
    });
    
    // Add any cards that weren't in columns (shouldn't happen, but safety check)
    allCards.forEach((card, id) => {
      if (!updatedCards.find(c => c.id === id)) {
        updatedCards.push(card);
      }
    });

    try {
      await updateDoc(doc(db, 'projects', experienceId), {
        decisionCards: updatedCards,
        updatedAt: serverTimestamp()
      });
      
      // Clear activeId after successful update
      setActiveId(null);
      originalSourceRef.current = { column: null, index: -1 };
      
      // Wait a bit before allowing reorganization to ensure Firestore has propagated
      setTimeout(() => {
        isUpdatingRef.current = false;
      }, 100);
    } catch (error) {
      console.error('Error updating card column:', error);
      toast.error('Failed to move card');
      // Revert local state on error - reorganize from Firestore
      isUpdatingRef.current = false;
      setActiveId(null);
      originalSourceRef.current = { column: null, index: -1 };
      const organized = {
        later: { title: 'Later', cards: [] },
        'to-discuss': { title: 'To Discuss', cards: [] },
        'in-discussion': { title: 'In Discussion', cards: [] },
        decided: { title: 'Decided', cards: [] }
      };

      const decisionCards = experience.decisionCards || [];
      decisionCards
        .filter(card => card.status !== 'dismissed')
        .forEach(card => {
          const column = card.kanbanColumn || card.status || 'to-discuss';
          if (organized[column]) {
            organized[column].cards.push(card);
          }
        });

      setColumns(organized);
    }
  };

  const proceedToExecute = async () => {
    // Check if all required decisions are decided
    const undecidedRequired = (experience.decisionCards || []).filter(
      card => card.required && card.status !== 'decided'
    );

    if (undecidedRequired.length > 0) {
      toast.error(`Please decide on: ${undecidedRequired.map(c => c.title).join(', ')}`);
      return;
    }

    // Generate tasks from decisions
    const tasks = generateTasksFromDecisions(experience.decisionCards || []);

    try {
      await updateDoc(doc(db, 'projects', experienceId), {
        currentPhase: 'execute',
        tasks: tasks,
        updatedAt: serverTimestamp()
      });

      toast.success('Moving to execution phase!');
    } catch (error) {
      console.error('Error moving to execute phase:', error);
      toast.error('Failed to move to execution phase');
    }
  };

  const getUserName = (userId) => {
    if (!userId || !family?.members) return 'Unknown';
    if (userId === user?.uid) return 'You';
    // Try to get name from family data
    return family.onboarding?.partnerName || `Family Member`;
  };

  const getUserInitials = (userId) => {
    const name = getUserName(userId);
    return name.charAt(0).toUpperCase();
  };

  return (
    <div className="min-h-screen bg-background pb-20">
      <div className="max-w-7xl mx-auto px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-serif font-bold text-text-primary mb-2">
              Discuss & Decide Together
            </h1>
            <p className="text-text-secondary">
              Collaborate with your family to finalize all decisions
            </p>
          </div>

          <button
            onClick={proceedToExecute}
            className="bg-gradient-to-r from-primary to-primary-light text-white font-bold px-8 py-4 rounded-xl hover:shadow-lg transition-all"
          >
            Move to Execution →
          </button>
        </div>

        {/* Kanban board */}
        <DndContext 
          sensors={sensors} 
          collisionDetection={closestCenter} 
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {Object.entries(columns).map(([columnId, column]) => (
              <DroppableColumn
                key={columnId}
                columnId={columnId}
                column={column}
                onCardClick={setSelectedCard}
                getUserName={getUserName}
                getUserInitials={getUserInitials}
              />
            ))}
          </div>
          <DragOverlay>
            {activeId ? (
              <div className="bg-white rounded-xl p-4 border-2 border-primary shadow-2xl opacity-90 rotate-2">
                {(() => {
                  const card = Object.values(columns)
                    .flatMap(col => col.cards)
                    .find(c => c.id === activeId);
                  return card ? (
                    <>
                      <h4 className="font-bold text-sm text-text-primary mb-2">
                        {card.title}
                      </h4>
                      {card.approvalRequired?.length > 0 && (
                        <div className="flex items-center gap-2 mb-2">
                          {card.approvalRequired.map(userId => {
                            const approved = card.approvedBy?.includes(userId);
                            return (
                              <div
                                key={userId}
                                className={`
                                  w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold
                                  ${approved ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}
                                `}
                              >
                                {approved ? '✓' : getUserInitials(userId)}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </>
                  ) : null;
                })()}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>

      {/* Card detail modal with comments & approvals */}
      {selectedCard && (
        <DiscussCardDetailModal
          card={selectedCard}
          experience={experience}
          onClose={() => setSelectedCard(null)}
          onUpdate={async (updates) => {
            const updatedCards = (experience.decisionCards || []).map(c =>
              c.id === selectedCard.id ? { ...c, ...updates } : c
            );
            
            try {
              await updateDoc(doc(db, 'projects', experienceId), {
                decisionCards: updatedCards,
                updatedAt: serverTimestamp()
              });

              setSelectedCard({ ...selectedCard, ...updates });
            } catch (error) {
              console.error('Error updating card:', error);
              toast.error('Failed to update card');
            }
          }}
        />
      )}
    </div>
  );
}

// Droppable Column Component
function DroppableColumn({ columnId, column, onCardClick, getUserName, getUserInitials }) {
  const { setNodeRef: setDroppableRef } = useDroppable({
    id: `column-${columnId}`,
  });

  return (
    <div
      ref={setDroppableRef}
      className={`
        rounded-2xl p-4 min-h-[500px] transition-all
        ${columnId === 'later' ? 'bg-gray-50 opacity-60' : 'bg-white'}
        border-2 border-border
      `}
    >
      {/* Column header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-text-primary">
          {column.title}
        </h3>
        <span className="text-sm font-semibold text-text-secondary bg-gray-100 px-2 py-1 rounded-full">
          {column.cards.length}
        </span>
      </div>

      {/* Cards */}
      <SortableContext 
        id={`column-${columnId}`}
        items={column.cards.map(c => c.id)}
        strategy={verticalListSortingStrategy}
      >
        <div className="space-y-3">
          {column.cards.map((card) => (
            <SortableCard
              key={card.id}
              card={card}
              onClick={() => onCardClick(card)}
              getUserName={getUserName}
              getUserInitials={getUserInitials}
            />
          ))}
        </div>
      </SortableContext>
    </div>
  );
}

// Sortable Card Component
function SortableCard({ card, onClick, getUserName, getUserInitials }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: card.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleClick = (e) => {
    // Prevent click when dragging
    if (isDragging) return;
    onClick();
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      onClick={handleClick}
      className={`
        bg-white rounded-xl p-4 border-2 cursor-grab active:cursor-grabbing transition-all relative
        ${isDragging ? 'shadow-2xl border-primary z-50' : 'border-border hover:border-primary'}
      `}
    >
      <h4 className="font-bold text-sm text-text-primary mb-2">
        {card.title}
      </h4>

      {/* Approval status */}
      {card.approvalRequired?.length > 0 && (
        <div className="flex items-center gap-2 mb-2">
          {card.approvalRequired.map(userId => {
            const approved = card.approvedBy?.includes(userId);
            return (
              <div
                key={userId}
                className={`
                  w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold
                  ${approved ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}
                `}
                title={getUserName(userId)}
              >
                {approved ? '✓' : getUserInitials(userId)}
              </div>
            );
          })}
        </div>
      )}

      {/* Comments count */}
      {card.comments?.length > 0 && (
        <div className="text-xs text-text-secondary flex items-center gap-1">
          <FontAwesomeIcon icon={faComment} />
          {card.comments.length}
        </div>
      )}

      {/* Assigned tasks */}
      {card.assignedTo?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {card.assignedTo.map(userId => (
            <span key={userId} className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-full">
              {getUserName(userId)}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// Helper function to generate tasks from decisions
function generateTasksFromDecisions(decisionCards) {
  const tasks = [];
  
  decisionCards.forEach(card => {
    if (card.status === 'decided') {
      // Generate tasks based on decision type
      if (card.type === 'gallery' && card.selectedOption) {
        tasks.push({
          id: `task-${card.id}-book`,
          title: `Book ${card.selectedOption}`,
          completed: false,
          linkedDecision: card.id,
          dueDate: null
        });
      }
      
      if (card.type === 'options' && card.selectedOption) {
        tasks.push({
          id: `task-${card.id}-confirm`,
          title: `Confirm ${card.selectedOption}`,
          completed: false,
          linkedDecision: card.id,
          dueDate: null
        });
      }
      
      // Add more task generation logic based on decision types
    }
  });
  
  return tasks;
}
